import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchCurrentUserInfo, fetchProfile, updateProfile, updateUserEmail, fetchCountries, fetchStates, setReferralByCode, fetchMemberById, isAdminUser } from "../script/rpc";

export default function ProfilePage({ uid, password }) {
  const navigate = useNavigate();
  const { search } = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [partnerId, setPartnerId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const [formData, setFormData] = useState({
    name: "", street: "", street2: "", city: "", zip: "",
    phone: "", email: "", country_id: "", state_id: "", parent_id: "",
    mlm_sequence: "", sponsor_code: "",
  });

  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  const countryRef = useRef(null);
  const stateRef = useRef(null);
  const countryBtnRef = useRef(null);
  const stateBtnRef = useRef(null);

  const filteredCountries = countries.filter(c => c.name.toLowerCase().includes(countrySearch.toLowerCase()));
  const filteredStates = states.filter(s => s.name.toLowerCase().includes(stateSearch.toLowerCase()));

  const selectedCountry = countries.find(c => c.id === formData.country_id);
  const selectedState = states.find(s => s.id === formData.state_id);

  // Simplified open functions - dropdown always opens below
  const openCountry = () => {
    setShowCountryDropdown(v => !v);
    setCountrySearch("");
  };
  const openState = () => {
    if (!states.length) return;
    setShowStateDropdown(v => !v);
    setStateSearch("");
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setShowCountryDropdown(false);
        setCountrySearch("");
      }
      if (stateRef.current && !stateRef.current.contains(e.target)) {
        setShowStateDropdown(false);
        setStateSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const userInfo = await fetchCurrentUserInfo(uid, password);
        if (!alive) return;
        if (!userInfo?.partner_id) throw new Error("Could not load user partner account.");
        const pid = userInfo.partner_id[0];
        setPartnerId(pid);
        const [profile, countryList] = await Promise.all([
          fetchProfile(uid, password, pid),
          fetchCountries(uid, password),
        ]);
        if (!alive) return;
        setCountries(countryList);

        // Check if admin — admins skip the referral code requirement
        const adminCheck = await isAdminUser(uid, password);
        if (alive) setIsAdmin(adminCheck);

        let initialParentId = profile?.parent_id ? profile.parent_id[0] : "";
        
        let fetchedSponsorCode = "";
        if (initialParentId) {
          const sponsorProfile = await fetchMemberById(uid, password, initialParentId);
          if (sponsorProfile && sponsorProfile.mlm_sequence) {
            fetchedSponsorCode = sponsorProfile.mlm_sequence;
          }
        }

        if (profile) {
          setFormData({
            name: profile.name || "", street: profile.street || "", street2: profile.street2 || "",
            city: profile.city || "", zip: profile.zip || "", phone: profile.phone || "",
            email: userInfo?.email || profile.email || "",
            country_id: profile.country_id ? profile.country_id[0] : "",
            state_id: profile.state_id ? profile.state_id[0] : "",
            parent_id: initialParentId,
            mlm_sequence: profile.mlm_sequence || "",
            sponsor_code: fetchedSponsorCode,
          });

          if (profile.mlm_sequence) {
            window.history.replaceState(null, "", `/profile?referralcode=${profile.mlm_sequence}`);
          }

          if (profile.country_id) {
            const stateList = await fetchStates(uid, password, profile.country_id[0]);
            if (alive) setStates(stateList);
          }
        }
      } catch (err) {
        if (alive) setError(err.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [uid, password]);

  const handleCountryChange = async (cId) => {
    setFormData(prev => ({ ...prev, country_id: cId, state_id: "" }));
    setShowCountryDropdown(false);
    setCountrySearch("");
    if (cId) {
      const stateList = await fetchStates(uid, password, cId);
      setStates(stateList);
    } else {
      setStates([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: (name.endsWith("_id") && value) ? Number(value) : value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!partnerId) return;

    const scode = formData.sponsor_code;

    setSaving(true);
    setError("");

    if (scode && !isAdmin) {
      try {
        const isValid = await setReferralByCode(uid, password, partnerId, scode);
        if (!isValid) {
          setError("Invalid Referral Code. Double check the code and try again.");
          setSaving(false);
          return;
        }
      } catch (err) {
        setError("Failed to verify referral code.");
        setSaving(false);
        return;
      }
    }

    try {
      await updateProfile(uid, password, partnerId, {
        street: formData.street,
        street2: formData.street2,
        city: formData.city,
        zip: formData.zip,
        phone: formData.phone,
        country_id: formData.country_id || false,
        state_id: formData.state_id || false,
      });

      if (formData.email) {
        await updateUserEmail(uid, password, formData.email);
      }

      navigate("/tree", { replace: true });
    } catch (err) {
      setError("Failed to save profile: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f6fa" }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #e5e7eb", borderTopColor: "#6366f1", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Fixed dropdown style - ALWAYS below (no dynamic positioning)
  const dropdownStyle = {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
    zIndex: 999,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const searchInputStyle = {
    padding: "9px 12px",
    border: "none",
    borderBottom: "1px solid #f0f0f0",
    outline: "none",
    fontSize: 13,
    fontFamily: "inherit",
    background: "#fafafa",
    color: "#111",
    width: "100%",
    boxSizing: "border-box",
  };

  const ddItemStyle = {
    padding: "9px 14px",
    cursor: "pointer",
    fontSize: 13,
    color: "#1e1e2e",
    borderBottom: "1px solid #f5f5f5",
    transition: "background 0.1s",
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f5f6fa",
      fontFamily: "'DM Sans', 'Söhne', 'Inter', system-ui, sans-serif",
      padding: "32px 16px",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      <div style={{
        width: "100%",
        maxWidth: 680,
        background: "#ffffff",
        borderRadius: 20,
        boxShadow: "0 2px 24px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)",
        overflow: "visible",
      }}>

        {/* Header */}
        <div style={{
          padding: "32px 36px 24px",
          borderBottom: "1px solid #f0f0f0",
        }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#0f0f1a", letterSpacing: "-0.4px" }}>
            Complete Your Profile
          </h1>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} style={{ padding: "28px 36px 32px", display: "flex", flexDirection: "column", gap: 20, overflow: "visible" }}>

          {error && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 8, padding: "10px 14px",
              color: "#dc2626", fontSize: 13,
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}

          {/* Row: Name + Email */}
          <Row>
            <Field label="Name">
              <Input name="name" value={formData.name} onChange={handleChange} placeholder="Your full name" />
            </Field>
            <Field label="Email">
              <Input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" />
            </Field>
          </Row>

          {/* Row: Phone + Sponsor */}
          <Row>
            <Field label="Phone">
              <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
            </Field>
            {!isAdmin && (
              <Field label="Referral Code">
                <Input name="sponsor_code" value={formData.sponsor_code} onChange={handleChange} placeholder="Enter Referral code" required />
              </Field>
            )}
          </Row>

          {/* Street */}
          <Field label="Street Address">
            <Input name="street" value={formData.street} onChange={handleChange} placeholder="123 Main Street" />
          </Field>

          {/* Row: City + Zip */}
          <Row>
            <Field label="City">
              <Input name="city" value={formData.city} onChange={handleChange} placeholder="City" />
            </Field>
            <Field label="Postal Code">
              <Input name="zip" value={formData.zip} onChange={handleChange} placeholder="ZIP / Postcode" />
            </Field>
          </Row>

          {/* Row: Country + State */}
          <Row>
            <Field label="Country">
              <div ref={countryRef} style={{ position: "relative", width: "100%" }}>
                <SelectButton ref={countryBtnRef} onClick={openCountry} placeholder="Select country" value={selectedCountry?.name} />
                {showCountryDropdown && (
                  <div style={dropdownStyle}>
                    <input
                      autoFocus
                      style={searchInputStyle}
                      placeholder="Search country…"
                      value={countrySearch}
                      onChange={e => setCountrySearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                    <div style={{ maxHeight: 110, overflowY: "auto" }}>
                      {filteredCountries.length === 0
                        ? <EmptyMsg />
                        : filteredCountries.map(c => (
                          <div key={c.id} style={ddItemStyle}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8f8ff"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}
                            onClick={() => handleCountryChange(c.id)}>
                            {c.name}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </Field>
            <Field label="State">
              <div ref={stateRef} style={{ position: "relative", width: "100%" }}>
                <SelectButton
                  ref={stateBtnRef}
                  onClick={openState}
                  placeholder={states.length === 0 ? "Select a country first" : "Select state"}
                  value={selectedState?.name}
                  disabled={states.length === 0}
                />
                {showStateDropdown && states.length > 0 && (
                  <div style={dropdownStyle}>
                    <input
                      autoFocus
                      style={searchInputStyle}
                      placeholder="Search state…"
                      value={stateSearch}
                      onChange={e => setStateSearch(e.target.value)}
                      onClick={e => e.stopPropagation()}
                    />
                    <div style={{ maxHeight: 110, overflowY: "auto" }}>
                      {filteredStates.length === 0
                        ? <EmptyMsg />
                        : filteredStates.map(s => (
                          <div key={s.id} style={ddItemStyle}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8f8ff"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, state_id: s.id }));
                              setShowStateDropdown(false);
                              setStateSearch("");
                            }}>
                            {s.name}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </Field>
          </Row>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving || (!isAdmin && !formData.sponsor_code)}
            style={{
              marginTop: 4,
              width: "100%",
              padding: "13px",
              borderRadius: 10,
              border: "none",
              background: saving || (!isAdmin && !formData.sponsor_code) ? "#c4b5fd" : "#6366f1",
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: saving || (!isAdmin && !formData.sponsor_code) ? "not-allowed" : "pointer",
              letterSpacing: "-0.2px",
              transition: "background 0.15s, transform 0.1s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
            onMouseEnter={e => { if (!saving && (isAdmin || formData.sponsor_code)) e.currentTarget.style.background = "#4f46e5"; }}
            onMouseLeave={e => { if (!saving && (isAdmin || formData.sponsor_code)) e.currentTarget.style.background = "#6366f1"; }}
          >
            {saving
              ? <div style={{ width: 18, height: 18, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite" }} />
              : "Save & Continue →"
            }
          </button>
        </form>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* ── Helpers ── */

function Row({ children }) {
  return <div style={{ display: "flex", gap: 16, overflow: "visible" }}>{React.Children.map(children, c => <div style={{ flex: 1, minWidth: 0, overflow: "visible" }}>{c}</div>)}</div>;
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, overflow: "visible" }}>
      <label style={{ fontSize: 14, fontWeight: 500, color: "#374151", letterSpacing: "-0.1px" }}>{label}</label>
      <div style={{ overflow: "visible" }}>{children}</div>
    </div>
  );
}

const Input = React.forwardRef(({ style, ...props }, ref) => (
  <input
    ref={ref}
    {...props}
    style={{
      width: "100%",
      boxSizing: "border-box",
      padding: "10px 13px",
      borderRadius: 8,
      border: "1.5px solid #e5e7eb",
      fontSize: 14,
      fontFamily: "inherit",
      color: "#111827",
      background: "#fafafa",
      outline: "none",
      transition: "border-color 0.15s",
      ...style,
    }}
    onFocus={e => e.target.style.borderColor = "#6366f1"}
    onBlur={e => e.target.style.borderColor = "#e5e7eb"}
  />
));

const SelectButton = React.forwardRef(({ onClick, placeholder, value, disabled }, ref) => (
  <div
    ref={ref}
    onClick={!disabled ? onClick : undefined}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 13px",
      borderRadius: 8,
      border: "1.5px solid #e5e7eb",
      fontSize: 14,
      fontFamily: "inherit",
      background: disabled ? "#f3f4f6" : "#fafafa",
      color: value ? "#111827" : "#9ca3af",
      cursor: disabled ? "not-allowed" : "pointer",
      userSelect: "none",
      opacity: disabled ? 0.7 : 1,
    }}
  >
    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value || placeholder}</span>
    {!disabled && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, marginLeft: 8 }}><path d="M2 4l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
  </div>
));

function EmptyMsg() {
  return <div style={{ padding: "14px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No results found</div>;
}